import { Controller } from "@hotwired/stimulus";

// These sessions are fictional. This preview never connects to an agent.
export default class extends Controller {
  static targets = ["key", "command", "mode", "style", "device", "selected", "feedback", "request", "reset"];
  static values = { empty: String, approved: String, denied: String, opened: String, archived: String, prompt: String };

  connect() {
    // _head.erb opts out of Turbo snapshots with turbo-cache-control=no-cache.
    // Returning here starts from the server's ten fictional sessions.
    this.original = this.keyTargets.map((key) => ({ ...key.dataset }));
    this.statusLabels = { ...this.keyTargets[0].querySelector("[data-key-status]").dataset };
    this.reset();
    [...this.modeTargets, ...this.styleTargets, this.resetTarget].forEach((button) => { button.disabled = false; });
  }

  reset() {
    this.sessions = this.original.map((session) => ({ ...session }));
    this.mode = "mixed";
    this.style = "solid";
    this.selection = this.sessions[0].session;
    this.render();
  }

  changeMode(event) {
    this.mode = event.currentTarget.dataset.mode;
    const visible = this.visibleSessions();
    if (!visible.some((session) => session.session === this.selection)) this.selection = visible[0]?.session;
    this.render();
  }

  changeStyle(event) {
    this.style = event.currentTarget.dataset.style;
    this.render();
  }

  visibleSessions() {
    return this.sessions.filter((session) => !session.archived && (this.mode === "mixed" || session.agent === this.mode));
  }

  visibleSlots() {
    const visible = this.visibleSessions();
    return this.mode === "mixed" ? ["claude", "codex"].flatMap((agent) => {
      const row = visible.filter((session) => session.agent === agent);
      return Array.from({ length: 5 }, (_, index) => row[index]);
    }) : Array.from({ length: 10 }, (_, index) => visible[index]);
  }

  select(event) {
    this.selection = this.visibleSlots()[Number(event.currentTarget.dataset.slot)]?.session;
    this.render();
    const selected = this.visibleSessions().find((session) => session.session === this.selection);
    if (selected) this.feedbackTarget.textContent = `${this.openedValue} ${selected.title}`;
  }

  command(event) {
    const command = event.currentTarget.dataset.command;
    const visible = this.visibleSessions();
    const index = visible.findIndex((session) => session.session === this.selection);
    const selected = visible[index];
    if (!selected) return;
    if (command === "previous" || command === "next") {
      this.selection = visible[(index + (command === "next" ? 1 : -1) + visible.length) % visible.length].session;
      this.render();
    } else if (command === "archive") {
      if (selected.agent !== "codex") return;
      selected.archived = true;
      const remaining = this.visibleSessions();
      this.selection = remaining.find((session) => session.agent === selected.agent)?.session || remaining[0]?.session;
      this.render();
      this.feedbackTarget.textContent = `${this.archivedValue} ${selected.title}`;
    } else if ((command === "approve" || command === "deny") && selected.status === "waiting") {
      selected.status = "idle";
      this.render();
      this.feedbackTarget.textContent = `${command === "approve" ? this.approvedValue : this.deniedValue} ${selected.title}`;
    }
  }

  render() {
    const visible = this.visibleSessions();
    const slots = this.visibleSlots();
    const selected = visible.find((session) => session.session === this.selection);
    this.keyTargets.forEach((key, index) => {
      const session = slots[index];
      key.disabled = !session;
      key.dataset.agent = session?.agent || "empty";
      key.dataset.status = session?.status || "empty";
      key.setAttribute("aria-pressed", String(Boolean(session && session.session === this.selection)));
      key.querySelector("[data-key-agent]").textContent = session?.agent.toUpperCase() || "";
      key.querySelector("[data-key-title]").textContent = session?.title || this.emptyValue;
      key.querySelector("[data-key-project]").textContent = session?.project || "";
      const status = session ? this.statusLabels[`status${session.status[0].toUpperCase()}${session.status.slice(1)}`] : "";
      key.querySelector("[data-key-status]").textContent = status;
      key.setAttribute("aria-label", session ? `${session.agent === "claude" ? "Claude Code" : "Codex"}, ${session.title}, ${status}` : this.emptyValue);
    });
    this.modeTargets.forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.mode === this.mode)));
    this.styleTargets.forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.style === this.style)));
    this.deviceTarget.dataset.appearance = this.style;
    this.commandTargets.forEach((button) => {
      button.disabled = !selected || (["approve", "deny"].includes(button.dataset.command) && selected.status !== "waiting") ||
        (button.dataset.command === "archive" && selected.agent !== "codex");
    });
    this.selectedTarget.textContent = selected ? `${selected.agent === "claude" ? "Claude Code" : "Codex"} · ${selected.title}` : this.emptyValue;
    this.feedbackTarget.textContent = selected?.status === "waiting" ? this.promptValue : (selected ? this.statusLabels[`status${selected.status[0].toUpperCase()}${selected.status.slice(1)}`] : this.emptyValue);
    this.requestTarget.dataset.waiting = String(selected?.status === "waiting");
  }
}
